// =============================================================================
// ShortcutsOverlay - the "?" sheet (UI Excellence item 9).
//
// Keyboard affordances the product already has were invisible: Cmd/Ctrl+K,
// Ctrl+J inside a chart, arrow keys in tables. A product is only keyboard-first
// if the keys are discoverable. Portalled, because a transformed ancestor would
// clip it (see check-fixed-overlay-containment).
// =============================================================================
import { createPortal } from "react-dom";
import { C } from "../lib/tokens";
import { MOTION } from "../lib/motion";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
const MOD = isMac ? "Cmd" : "Ctrl";

const GROUPS = [
  ["Anywhere", [
    [MOD + " K", "Open the command palette: jump to a page, a patient, or run an action"],
    ["?", "Show this list"],
    ["Esc", "Close the topmost dialog, drawer, or palette"],
    ["Ctrl + Alt + = / - / 0", "Larger, smaller, or reset text size on this page (Cmd on Mac)"],
  ]],
  ["Lists and tables", [
    ["Up / Down", "Move between rows"],
    ["Enter", "Open the focused row"],
    ["Click a column", "Sort by it; click again to reverse"],
  ]],
  ["Patient chart", [
    [MOD + " J", "Jump to a section"],
    ["Scroll", "The context strip keeps the patient, tier, and AI actions in reach"],
  ]],
];

export default function ShortcutsOverlay({ open, onClose }) {
  if (!open) return null;
  const node = (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 4200,
        background: "rgba(var(--brand-primary-rgb, 15,110,86), 0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        animation: MOTION.reduced ? "none" : "pos-fadeIn 150ms ease",
      }}
    >
      <div className="pos-glass-floating" style={{
        width: "100%", maxWidth: 560, borderRadius: 16, padding: "20px 22px",
        maxHeight: "86vh", overflowY: "auto",
        animation: MOTION.reduced ? "none" : "pos-scaleIn 200ms " + MOTION.ease.standard,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.textPrimary }}>Keyboard shortcuts</div>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", cursor: "pointer",
            fontSize: 12, color: C.textSecondary, fontFamily: "inherit",
          }}>Close</button>
        </div>
        {GROUPS.map(([title, rows]) => (
          <div key={title} style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase",
              color: C.textTertiary, marginBottom: 8,
            }}>{title}</div>
            {rows.map(([keys, what]) => (
              <div key={keys} style={{ display: "flex", gap: 12, alignItems: "baseline", padding: "5px 0" }}>
                <kbd style={{
                  fontSize: 11, fontFamily: "inherit", fontWeight: 600,
                  border: "1px solid " + C.borderMid, borderRadius: 6, padding: "2px 7px",
                  color: C.textPrimary, flexShrink: 0, minWidth: 62, textAlign: "center",
                }}>{keys}</kbd>
                <span style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.5 }}>{what}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
}
